import React, { useEffect, useRef } from "react";

const cities = [
  { name: "New York",     rx: 0.215, ry: 0.435 },
  { name: "Los Angeles",  rx: 0.13,  ry: 0.445 },
  { name: "London",       rx: 0.455, ry: 0.275 },
  { name: "Paris",        rx: 0.468, ry: 0.295 },
  { name: "Dubai",        rx: 0.567, ry: 0.395 },
  { name: "Mumbai",       rx: 0.612, ry: 0.435 },
  { name: "Tokyo",        rx: 0.775, ry: 0.345 },
  { name: "Shanghai",     rx: 0.748, ry: 0.365 },
  { name: "Singapore",    rx: 0.715, ry: 0.495 },
  { name: "Sydney",       rx: 0.812, ry: 0.695 },
  { name: "Sao Paulo",    rx: 0.255, ry: 0.685 },
  { name: "Johannesburg", rx: 0.512, ry: 0.635 },
  { name: "Moscow",       rx: 0.548, ry: 0.225 },
  { name: "Seoul",        rx: 0.768, ry: 0.315 },
];

const allRoutes = [
  [0,2],[0,6],[0,10],[1,6],[1,8],[2,3],[2,4],[2,12],
  [3,4],[4,5],[4,11],[5,7],[5,8],[6,7],[6,13],[7,8],
  [8,9],[8,11],[9,6],[10,11],[12,6],[2,8],[0,4]
];

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function makeSlot() {
  return {
    from: 0, to: 1,
    active: false,
    phase: "idle",
    drawProgress: 0,
    dotProgress: 0,
    lineAlpha: 0,
    drawSpeed:  0.008 + Math.random() * 0.005,
    travelSpeed: 0.004 + Math.random() * 0.004,
    fadeSpeed:  0.012,
  };
}

const WorldMapAnimation = () => {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const stateRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const hero = canvas.parentElement;

    let isVisible = true;
    const visibilityObserver = new IntersectionObserver(
      ([entry]) => {
        isVisible = entry.isIntersecting;
      },
      { threshold: 0.1 }
    );
    visibilityObserver.observe(hero);

    function resize() {
      canvas.width = hero.offsetWidth * window.devicePixelRatio;
      canvas.height = hero.offsetHeight * window.devicePixelRatio;
      canvas.style.width = hero.offsetWidth + "px";
      canvas.style.height = hero.offsetHeight + "px";
    }
    resize();
    window.addEventListener("resize", resize);

    const MAX_ACTIVE = 3;
    const slots = Array.from({ length: MAX_ACTIVE }, makeSlot);
    let routeQueue = shuffle(allRoutes);
    let queueIdx = 0;
    let globalT = 0;
    const launchTimes = [0, 0.6, 1.3];
    const cityRings = cities.map(() => ({ r: 0, alpha: 0, active: false }));

    function nextRoute() {
      if (queueIdx >= routeQueue.length) {
        routeQueue = shuffle(allRoutes);
        queueIdx = 0;
      }
      const r = routeQueue[queueIdx++];
      return Math.random() > 0.5 ? [r[0], r[1]] : [r[1], r[0]];
    }

    function launchSlot(slot) {
      const r = nextRoute();
      slot.from = r[0];
      slot.to = r[1];
      slot.phase = "drawing";
      slot.drawProgress = 0;
      slot.dotProgress = 0;
      slot.lineAlpha = 1.0;
      slot.drawSpeed = 0.008 + Math.random() * 0.005;
      slot.travelSpeed = 0.004 + Math.random() * 0.004;
      slot.active = true;
    }

    function getPos(i) {
      return {
        x: cities[i].rx * hero.offsetWidth,
        y: cities[i].ry * hero.offsetHeight,
      };
    }

    function getArc(ax, ay, bx, by) {
      const mx = (ax + bx) / 2;
      const dist = Math.hypot(bx - ax, by - ay);
      const my = Math.min(ay, by) - dist * 0.28;
      return { mx, my };
    }

    function bezier(ax, ay, cx, cy, bx, by, t) {
      return {
        x: (1 - t) * (1 - t) * ax + 2 * (1 - t) * t * cx + t * t * bx,
        y: (1 - t) * (1 - t) * ay + 2 * (1 - t) * t * cy + t * t * by,
      };
    }

    function drawPartialArc(ax, ay, cx, cy, bx, by, p, alpha) {
      const steps = 80;
      const end = Math.floor(p * steps);
      if (end < 1) return;

      ctx.beginPath();
      ctx.moveTo(ax, ay);
      for (let i = 1; i <= end; i++) {
        const pt = bezier(ax, ay, cx, cy, bx, by, i / steps);
        ctx.lineTo(pt.x, pt.y);
      }
      ctx.strokeStyle = `rgba(200,155,40,${alpha * 0.35})`;
      ctx.lineWidth = 0.7;
      ctx.stroke();

      if (end >= 3) {
        const t0 = (end - 3) / steps;
        const t1 = end / steps;
        const p0 = bezier(ax, ay, cx, cy, bx, by, t0);
        const p1 = bezier(ax, ay, cx, cy, bx, by, t1);
        ctx.beginPath();
        ctx.moveTo(p0.x, p0.y);
        ctx.lineTo(p1.x, p1.y);
        ctx.strokeStyle = `rgba(255,215,70,${alpha})`;
        ctx.lineWidth = 1.8;
        ctx.stroke();
      }
    }

    function updateSlot(slot) {
      const fp = getPos(slot.from), tp = getPos(slot.to);
      const { mx, my } = getArc(fp.x, fp.y, tp.x, tp.y);

      if (slot.phase === "drawing") {
        slot.drawProgress += slot.drawSpeed;
        if (slot.drawProgress >= 1) {
          slot.drawProgress = 1;
          slot.phase = "traveling";
          slot.dotProgress = 0;
        }
        drawPartialArc(fp.x, fp.y, mx, my, tp.x, tp.y, slot.drawProgress, slot.lineAlpha);
      } else if (slot.phase === "traveling") {
        slot.dotProgress += slot.travelSpeed;
        drawPartialArc(fp.x, fp.y, mx, my, tp.x, tp.y, 1, slot.lineAlpha * 0.45);

        const dp = Math.min(slot.dotProgress, 1);
        const pt = bezier(fp.x, fp.y, mx, my, tp.x, tp.y, dp);

        // Comet tail
        const tailLen = 20;
        for (let k = tailLen; k >= 1; k--) {
          const tp2 = Math.max(0, dp - k * 0.012);
          const tpt = bezier(fp.x, fp.y, mx, my, tp.x, tp.y, tp2);
          const ta = (1 - k / tailLen) * 0.65;
          const tr = (1 - k / tailLen) * 3.5;
          ctx.beginPath();
          ctx.arc(tpt.x, tpt.y, tr, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(255,200,60,${ta})`;
          ctx.fill();
        }

        // Bright head
        const grd = ctx.createRadialGradient(pt.x, pt.y, 0, pt.x, pt.y, 12);
        grd.addColorStop(0, "rgba(255,245,160,1)");
        grd.addColorStop(0.25, "rgba(255,200,60,0.8)");
        grd.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = grd;
        ctx.beginPath();
        ctx.arc(pt.x, pt.y, 12, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(pt.x, pt.y, 2.5, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(255,245,180,1)";
        ctx.fill();

        if (slot.dotProgress >= 1) {
          slot.phase = "fading";
          slot.lineAlpha = 0.45;
          cityRings[slot.to].r = 0;
          cityRings[slot.to].alpha = 1;
          cityRings[slot.to].active = true;
        }
      } else if (slot.phase === "fading") {
        slot.lineAlpha -= slot.fadeSpeed;
        if (slot.lineAlpha > 0) {
          drawPartialArc(fp.x, fp.y, mx, my, tp.x, tp.y, 1, slot.lineAlpha);
        } else {
          launchSlot(slot);
        }
      }
    }

    function animate() {
      if (!isVisible) {
        animRef.current = requestAnimationFrame(animate);
        return;
      }
      const W = hero.offsetWidth;
      const H = hero.offsetHeight;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.setTransform(window.devicePixelRatio, 0, 0, window.devicePixelRatio, 0, 0);
      globalT += 0.016;

      slots.forEach((slot, i) => {
        if (!slot.active && globalT >= launchTimes[i]) {
          launchSlot(slot);
        }
      });

      slots.forEach((slot) => {
        if (slot.active) updateSlot(slot);
      });

      cities.forEach((c, i) => {
        const cx = c.rx * W, cy = c.ry * H;
        const pulse = 0.65 + 0.35 * Math.sin(globalT * 1.0 + i * 0.85);

        const halo = ctx.createRadialGradient(cx, cy, 0, cx, cy, 20);
        halo.addColorStop(0, `rgba(255,185,50,${0.4 * pulse})`);
        halo.addColorStop(0.5, `rgba(200,135,25,${0.12 * pulse})`);
        halo.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = halo;
        ctx.beginPath();
        ctx.arc(cx, cy, 20, 0, Math.PI * 2);
        ctx.fill();

        ctx.beginPath();
        ctx.arc(cx, cy, 2.5, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,230,120,${pulse})`;
        ctx.fill();

        const ring = cityRings[i];
        if (ring.active) {
          ring.r += 1.2;
          ring.alpha -= 0.014;
          if (ring.alpha <= 0) {
            ring.active = false;
            ring.alpha = 0;
          }
          ctx.beginPath();
          ctx.arc(cx, cy, ring.r, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(255,210,80,${ring.alpha})`;
          ctx.lineWidth = 1.5;
          ctx.stroke();
          if (ring.r > 10) {
            ctx.beginPath();
            ctx.arc(cx, cy, ring.r - 10, 0, Math.PI * 2);
            ctx.strokeStyle = `rgba(255,175,45,${ring.alpha * 0.45})`;
            ctx.lineWidth = 0.8;
            ctx.stroke();
          }
        }
      });

      animRef.current = requestAnimationFrame(animate);
    }

    animate();

    return () => {
      window.removeEventListener("resize", resize);
      cancelAnimationFrame(animRef.current);
      visibilityObserver.disconnect();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 0,
      }}
    />
  );
};

export default WorldMapAnimation;
